import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AttemptFeedback, PublicProblem, WorkedAnswerDraft } from "../shared/domain";
import { MODEL_LABELS } from "../shared/domain";
import { ApiError, client, type DashboardView, type HistoryEntry, type PracticeView } from "./api";

const emptyDraft = (): WorkedAnswerDraft => ({ equations: ["", ""], finalAnswer: "", unit: "" });

function Loading() {
  return <main className="center-stage"><div className="loader" aria-label="Loading" /><p>Preparing today’s thinking…</p></main>;
}

function ErrorView({ message, retry }: { message: string; retry: () => void }) {
  return <main className="center-stage"><span className="big-icon">☁️</span><h1>We couldn’t load the practice.</h1><p>{message}</p><button className="primary" onClick={retry}>Try again</button></main>;
}

function ProgressDots({ problems, active }: { problems: PublicProblem[]; active: number }) {
  return <div className="progress-dots" aria-label={`${problems.filter((problem) => problem.status === "solved").length} of 5 solved`}>
    {problems.map((problem, index) => <span key={problem.id} className={`progress-dot ${problem.status} ${index === active ? "active" : ""}`}><b>{problem.status === "solved" ? "✓" : index + 1}</b></span>)}
  </div>;
}

type FocusTarget = { kind: "equation"; index: number } | { kind: "answer" };

function MathKeypad({ onKey }: { onKey: (key: string) => void }) {
  const keys = ["7", "8", "9", "÷", "(", "4", "5", "6", "×", ")", "1", "2", "3", "−", "R", "0", "+", "=", "⌫", "Clear"];
  return <div className="math-keypad" aria-label="Math keypad">
    {keys.map((key) => <button key={key} type="button" className={key === "Clear" ? "wide-key" : ""} onClick={() => onKey(key)}>{key}</button>)}
  </div>;
}

function PracticeCard({ problem, onSolved, onDeferred }: { problem: PublicProblem; onSolved: (feedback: AttemptFeedback) => void; onDeferred: () => void }) {
  const [draft, setDraft] = useState<WorkedAnswerDraft>(problem.draft ?? emptyDraft());
  const [feedback, setFeedback] = useState<AttemptFeedback | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [focus, setFocus] = useState<FocusTarget>({ kind: "equation", index: 0 });
  const initialized = useRef(false);

  useEffect(() => {
    setDraft(problem.draft ?? emptyDraft());
    setFeedback(null);
    initialized.current = false;
  }, [problem.id, problem.draft]);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }
    const timeout = window.setTimeout(() => { void client.saveDraft(problem.id, draft).catch(() => undefined); }, 700);
    return () => window.clearTimeout(timeout);
  }, [draft, problem.id]);

  const changeEquation = (index: number, value: string) => {
    setDraft((current) => ({ ...current, equations: current.equations.map((equation, equationIndex) => equationIndex === index ? value : equation) }));
  };

  const keypad = (key: string) => {
    const apply = (value: string) => key === "Clear" ? "" : key === "⌫" ? value.slice(0, -1) : `${value}${key}`;
    setDraft((current) => focus.kind === "answer"
      ? { ...current, finalAnswer: apply(current.finalAnswer) }
      : { ...current, equations: current.equations.map((equation, index) => index === focus.index ? apply(equation) : equation) });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const result = await client.attempt(problem.id, draft);
      setFeedback(result);
      if (result.correct) onSolved(result);
    } catch (error) {
      setFeedback({ correct: false, assisted: false, message: error instanceof Error ? error.message : "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = draft.equations.filter((equation) => equation.trim()).length >= 2 && draft.finalAnswer.trim() !== "" && draft.unit.trim() !== "";

  return <article className="practice-card">
    <div className="model-chip">{MODEL_LABELS[problem.model]}</div>
    <h2>{problem.prompt}</h2>
    <div className="work-area">
      <div className="field-heading"><span>Your equations</span><button type="button" className="text-button" onClick={() => setDraft((current) => ({ ...current, equations: current.equations.length < 4 ? [...current.equations, ""] : current.equations }))}>+ Add step</button></div>
      {draft.equations.map((equation, index) => <div className="equation-row" key={index}>
        <span>{index + 1}</span>
        <input aria-label={`Equation ${index + 1}`} inputMode="none" value={equation} onFocus={() => setFocus({ kind: "equation", index })} onChange={(event) => changeEquation(index, event.target.value)} placeholder="Write an equation" />
        {draft.equations.length > 2 && <button aria-label="Remove step" className="remove-step" onClick={() => setDraft((current) => ({ ...current, equations: current.equations.filter((_, equationIndex) => equationIndex !== index) }))}>×</button>}
      </div>)}
      <div className="answer-row">
        <label>Final answer<input inputMode="none" value={draft.finalAnswer} onFocus={() => setFocus({ kind: "answer" })} onChange={(event) => setDraft((current) => ({ ...current, finalAnswer: event.target.value }))} placeholder="Number" /></label>
        <label>Unit<input value={draft.unit} onChange={(event) => setDraft((current) => ({ ...current, unit: event.target.value }))} placeholder="e.g. books" /></label>
      </div>
      <MathKeypad onKey={keypad} />
    </div>
    {feedback && <div className={`feedback ${feedback.correct ? "success" : "needs-work"}`} role="status">
      <strong>{feedback.correct ? "Well reasoned!" : "Take another look"}</strong>
      <p>{feedback.message}</p>
      {feedback.solution && <ol>{feedback.solution.map((step) => <li key={step}>{step}</li>)}</ol>}
    </div>}
    <div className="card-actions">
      <button className="secondary" onClick={onDeferred}>Do later</button>
      <button className="primary" disabled={!canSubmit || submitting} onClick={() => { void submit(); }}>{submitting ? "Checking…" : "Check my reasoning"}</button>
    </div>
  </article>;
}

function Celebration({ practice, onHistory }: { practice: PracticeView; onHistory: () => void }) {
  return <main className="celebration">
    <div className="badge-burst"><span>✦</span><div>🏅</div><span>✦</span></div>
    <p className="eyebrow">Daily practice complete</p>
    <h1>Strong thinking, Zinuo!</h1>
    <p>You turned five stories into clear mathematical steps.</p>
    <div className="earned-badge"><small>Badge earned</small><strong>{(practice.badgeId ?? "daily-diamond").replaceAll("-", " ")}</strong></div>
    <button className="primary" onClick={onHistory}>See my journey</button>
  </main>;
}

function HistoryPanel({ history, close }: { history: HistoryEntry[]; close: () => void }) {
  return <div className="drawer-backdrop" onClick={close}><aside className="history-drawer" onClick={(event) => event.stopPropagation()}>
    <header><div><p className="eyebrow">Your journey</p><h2>Practice history</h2></div><button className="icon-button" onClick={close}>×</button></header>
    <div className="history-list">{history.length === 0 ? <p>No completed days yet.</p> : history.map((entry) => <div className="history-item" key={entry.date}>
      <span className={`history-status ${entry.status}`}>{entry.status === "completed" ? "✓" : entry.solved}</span>
      <div><strong>{new Date(`${entry.date}T00:00:00`).toLocaleDateString("en", { month: "long", day: "numeric" })}</strong><small>{entry.status === "completed" ? `${entry.assisted} assisted` : `${entry.solved} of 5 solved`}</small></div>
      {entry.badgeId && <span className="mini-badge">🏅</span>}
    </div>)}</div>
  </aside></div>;
}

function LearnerApp() {
  const [practice, setPractice] = useState<PracticeView | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [active, setActive] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [today, past] = await Promise.all([client.today(), client.history()]);
      setPractice(today);
      setHistory(past);
      const firstOpen = today.problems.findIndex((problem) => problem.status !== "solved");
      setActive(firstOpen === -1 ? 0 : firstOpen);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Please check the internet connection.");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  if (error) return <ErrorView message={error} retry={() => { void load(); }} />;
  if (!practice) return <Loading />;
  if (practice.status === "completed") return <><Celebration practice={practice} onHistory={() => setShowHistory(true)} />{showHistory && <HistoryPanel history={history} close={() => setShowHistory(false)} />}</>;
  const problem = practice.problems[active];
  if (!problem) return <ErrorView message="Today’s practice is incomplete." retry={() => { void load(); }} />;

  const solved = async (feedback: AttemptFeedback) => {
    if (feedback.practiceCompleted) {
      await load();
      return;
    }
    setPractice((current) => current ? { ...current, problems: current.problems.map((item) => item.id === problem.id ? { ...item, status: "solved", assisted: feedback.assisted } : item) } : current);
    const next = practice.problems.findIndex((item, index) => index > active && item.status !== "solved");
    window.setTimeout(() => setActive(next >= 0 ? next : practice.problems.findIndex((item) => item.status !== "solved" && item.id !== problem.id)), 650);
  };

  const defer = async () => {
    await client.defer(problem.id);
    const next = practice.problems.findIndex((item, index) => index > active && item.status !== "solved");
    setActive(next >= 0 ? next : 0);
  };

  return <div className="learner-shell">
    <header className="topbar"><a className="brand" href="/"><span className="brand-mark">Z</span><div><strong>Zinuo’s Daily Math</strong><small>Five thoughtful problems</small></div></a><button className="journey-button" onClick={() => setShowHistory(true)}>Journey <span>↗</span></button></header>
    <main className="practice-layout">
      <div className="practice-meta"><div><p className="eyebrow">Practice {practice.sequence}</p><h1>{new Date(`${practice.date}T00:00:00`).toLocaleDateString("en", { weekday: "long", month: "long", day: "numeric" })}</h1></div><ProgressDots problems={practice.problems} active={active} /></div>
      <PracticeCard key={problem.id} problem={problem} onSolved={(feedback) => { void solved(feedback); }} onDeferred={() => { void defer(); }} />
    </main>
    <footer><span>Think slowly. Show every step.</span><a href="/parent">Parent</a></footer>
    {showHistory && <HistoryPanel history={history} close={() => setShowHistory(false)} />}
  </div>;
}

function ParentLogin({ onLogin }: { onLogin: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await client.parentLogin(password);
      onLogin();
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "登录失败");
    }
  };
  return <main className="parent-login"><a className="brand" href="/"><span className="brand-mark">Z</span><strong>Zinuo’s Daily Math</strong></a><form onSubmit={(event) => { void submit(event); }}><p className="eyebrow">家长空间</p><h1>查看学习进展</h1><p>输入家长密码继续。</p><label>密码<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>{error && <p className="form-error">{error}</p>}<button className="primary" type="submit">登录</button></form></main>;
}

function Metric({ label, value, note }: { label: string; value: string; note: string }) {
  return <div className="metric"><small>{label}</small><strong>{value}</strong><span>{note}</span></div>;
}

function ParentDashboard({ dashboard, reload }: { dashboard: DashboardView; reload: () => Promise<void> }) {
  const [settings, setSettings] = useState(dashboard.settings);
  const [clearPassword, setClearPassword] = useState("");
  const totalCorrect = dashboard.recent.independent + dashboard.recent.assisted;
  const independentRate = totalCorrect === 0 ? 0 : Math.round(dashboard.recent.independent / totalCorrect * 100);
  const mastered = dashboard.models.filter((model) => model.mastered).length;
  const errorTotal = Object.values(dashboard.errors).reduce((sum, value) => sum + value, 0);

  const saveSettings = async () => {
    await client.updateSettings(settings);
    await reload();
  };

  const flag = async (id: string) => {
    const reason = window.prompt("问题在哪里？可留空。") ?? "";
    await client.flag(id, reason);
    await reload();
  };

  const clear = async () => {
    if (!window.confirm("永久清空全部练习记录？此操作无法撤销。")) return;
    await client.clearData(clearPassword);
    setClearPassword("");
    await reload();
  };

  return <div className="parent-shell">
    <header className="parent-topbar"><a className="brand" href="/"><span className="brand-mark">Z</span><div><strong>Zinuo’s Daily Math</strong><small>家长空间</small></div></a><button className="text-button" onClick={() => { void client.parentLogout().then(() => window.location.reload()); }}>退出</button></header>
    <main className="dashboard">
      <div className="dashboard-heading"><div><p className="eyebrow">学习概览</p><h1>大诺的进展</h1></div><span className="today-pill">今日 {dashboard.today.solved}/5</span></div>
      <section className="metrics-grid"><Metric label="今日状态" value={dashboard.today.status === "completed" ? "已完成" : `${dashboard.today.solved}/5`} note="日本时间" /><Metric label="7 天独立完成" value={`${independentRate}%`} note={`${dashboard.recent.assisted} 题使用解析`} /><Metric label="已掌握模型" value={`${mastered}/7`} note={`下一重点：${dashboard.nextFocus}`} /><Metric label="7 天错误" value={String(errorTotal)} note="按原因归类" /></section>

      <div className="dashboard-columns">
        <section className="panel"><div className="panel-heading"><div><p className="eyebrow">Mastery Path</p><h2>模型掌握</h2></div></div><div className="model-list">{dashboard.models.map((model) => <div className="model-row" key={model.id}><span className={model.mastered ? "mastered" : "learning"}>{model.mastered ? "✓" : model.successes}</span><div><strong>{model.label}</strong><small>{model.mastered ? "已掌握" : `${model.successes}/3 次跨日独立答对`}</small></div></div>)}</div></section>
        <section className="panel"><div className="panel-heading"><div><p className="eyebrow">最近 7 天</p><h2>错误趋势</h2></div></div><div className="error-bars">{(["relationship", "operation", "calculation", "unit"] as const).map((key) => <div key={key}><span>{({ relationship: "数量关系", operation: "运算选择", calculation: "计算", unit: "单位" })[key]}</span><div><i style={{ width: `${errorTotal === 0 ? 0 : Math.max(8, dashboard.errors[key] / errorTotal * 100)}%` }} /></div><b>{dashboard.errors[key]}</b></div>)}</div></section>
      </div>

      <section className="panel settings-panel"><div><p className="eyebrow">练习控制</p><h2>自动进阶</h2></div><label className="switch-row"><span><strong>暂停新模型</strong><small>继续复习已学内容</small></span><input type="checkbox" checked={settings.pauseNewModels} onChange={(event) => setSettings((current) => ({ ...current, pauseNewModels: event.target.checked }))} /></label><label><span className="control-label">临时难度</span><select value={settings.difficultyShift} onChange={(event) => setSettings((current) => ({ ...current, difficultyShift: Number(event.target.value) as -1 | 0 | 1 }))}><option value={-1}>降低一级</option><option value={0}>自动</option><option value={1}>提高一级</option></select></label><button className="primary compact" onClick={() => { void saveSettings(); }}>保存设置</button></section>

      <section className="panel"><div className="panel-heading"><div><p className="eyebrow">质量检查</p><h2>最近题目</h2></div></div><div className="recent-problems">{dashboard.recentProblems.map((problem) => <div key={problem.id}><div><small>{problem.date} · {problem.model}</small><p>{problem.prompt}</p><span>{problem.attempts} 次尝试 · {problem.status}</span></div><button className="flag-button" onClick={() => { void flag(problem.id); }}>标记问题</button></div>)}</div></section>

      <section className="panel data-panel"><div><p className="eyebrow">数据</p><h2>导出或清空</h2><p>导出包含练习、作答与掌握记录，不包含身份信息。</p></div><div className="data-actions"><a className="secondary button-link" href="/api/parent/export">下载 JSON</a><input type="password" placeholder="再次输入家长密码" value={clearPassword} onChange={(event) => setClearPassword(event.target.value)} /><button className="danger" disabled={!clearPassword} onClick={() => { void clear(); }}>永久清空</button></div></section>
    </main>
  </div>;
}

function ParentApp() {
  const [dashboard, setDashboard] = useState<DashboardView | null>(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setError("");
    try {
      setDashboard(await client.dashboard());
      setNeedsLogin(false);
    } catch (loadError) {
      if (loadError instanceof ApiError && loadError.status === 401) setNeedsLogin(true);
      else setError(loadError instanceof Error ? loadError.message : "加载失败");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  if (needsLogin) return <ParentLogin onLogin={() => { void load(); }} />;
  if (error) return <ErrorView message={error} retry={() => { void load(); }} />;
  if (!dashboard) return <Loading />;
  return <ParentDashboard dashboard={dashboard} reload={load} />;
}

export function App() {
  const parent = useMemo(() => window.location.pathname.startsWith("/parent"), []);
  return parent ? <ParentApp /> : <LearnerApp />;
}

